import type { CustomerSpdState, CustomerSpdResponseItem, CustomerSpd } from '@makemydeal/dr-dash-types';
import type { Reducer } from 'redux';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import {
    CHECK_CUSTOMER_SPD_FAILURE,
    CHECK_CUSTOMER_SPD_REQUEST,
    CHECK_CUSTOMER_SPD_SUCCESS,
    UPDATE_CUSTOMER_SPD,
    UPDATE_CUSTOMER_SPD_FAILURE,
    UPDATE_CUSTOMER_SPD_SUCCESS,
    UPDATE_CUSTOMER_SPD_REQUEST,
    CUSTOMER_SPD_FAILURE,
    CUSTOMER_SPD_SUCCESS,
    CUSTOMER_SPD_REQUEST,
    CLEAR_CUSTOMER_SPD_ERROR,
    REMOVE_COBUYER_SPD
} from '../actionTypes/CustomerSpdTypes';
import { mergeNonEmptyFields } from '@makemydeal/dr-dash-ui-utils';
import { HIDE_EDIT_CUSTOMER_SLIDEOUT, SHOW_EDIT_CUSTOMER_SLIDEOUT } from '../actionTypes/editCustomerActionTypes';

export const customerSpdInitialState: CustomerSpdState = {
    checkCustomerLoading: false,
    checkCustomerResponse: {},
    checkCustomerError: null,
    canDesk: false,
    participantsSpd: {},
    updateCustomerSpdLoading: false,
    updateCustomerSpdResponse: {},
    updateCustomerSpdError: null,
    getCustomerLoading: false,
    getCustomerResponse: {},
    getCustomerError: {},
    getCustomerAPISuccess: {}
};

export type CustomerSpdReducer = Reducer<CustomerSpdState>;

const DEFAULT_CHECK_CUSTOMER_SPD_RESPONSE: CustomerSpdResponseItem = {
    socialSecurityNumber: false,
    dateOfBirth: false,
    driversLicenseNumber: false,
    driversLicenseState: null,
    driversLicenseExpirationDate: false,
    bankAccountNumber: false
};

const removeKeyFromRecord = <T>(record: Record<string, T>, keyToRemove: string): Record<string, T> => {
    const nextRecord = { ...record };
    delete nextRecord[keyToRemove];
    return nextRecord;
};

export const reducer: CustomerSpdReducer = (state: CustomerSpdState = customerSpdInitialState, action: AnyFSA) => {
    switch (action.type) {
        case REMOVE_COBUYER_SPD: {
            const commonConsumerId = action.payload as string;
            if (!commonConsumerId) {
                return state;
            }

            return {
                ...state,
                checkCustomerResponse: removeKeyFromRecord(state.checkCustomerResponse, commonConsumerId),
                participantsSpd: removeKeyFromRecord(state.participantsSpd, commonConsumerId),
                updateCustomerSpdResponse: removeKeyFromRecord(state.updateCustomerSpdResponse as any, commonConsumerId),
                getCustomerResponse: removeKeyFromRecord(state.getCustomerResponse, commonConsumerId),
                getCustomerError: removeKeyFromRecord(state.getCustomerError as any, commonConsumerId),
                getCustomerAPISuccess: removeKeyFromRecord(state.getCustomerAPISuccess, commonConsumerId)
            };
        }

        case CHECK_CUSTOMER_SPD_FAILURE: {
            return {
                ...state,
                checkCustomerLoading: false,
                checkCustomerError: action.payload
            };
        }
        case CHECK_CUSTOMER_SPD_SUCCESS: {
            if (action.payload?.ok === false) {
                // Backend returns ok: false when DealXG responds with 404 (deal SPD not found).
                // Default all SPD fields to false for each commonConsumerId.
                const commonConsumerIds: string[] = action.meta.commonConsumerIds;
                const updatedCheckCustomerResponse = { ...state.checkCustomerResponse };
                commonConsumerIds.forEach((ccid) => {
                    updatedCheckCustomerResponse[ccid] = { ...DEFAULT_CHECK_CUSTOMER_SPD_RESPONSE };
                });
                return {
                    ...state,
                    checkCustomerLoading: false,
                    checkCustomerResponse: updatedCheckCustomerResponse
                };
            }
            // canDesk is a flat sibling of the CCID keys (not per-participant data) — pull it out before
            // treating the rest of the payload as the CCID -> presence map.
            const { canDesk, ...responseMap } = (action.payload ?? {}) as Record<string, CustomerSpdResponseItem> & {
                canDesk?: boolean;
            };
            const updatedCheckCustomerResponse = { ...state.checkCustomerResponse, ...responseMap };
            const updatedParticipantsSpd = { ...state.participantsSpd };
            Object.keys(responseMap).forEach((ccid) => {
                const response = responseMap[ccid];
                const getCustomerAPISuccess = state.getCustomerAPISuccess[ccid] || false;
                if (response?.driversLicenseState && !getCustomerAPISuccess) {
                    updatedParticipantsSpd[ccid] = {
                        ...updatedParticipantsSpd[ccid],
                        driversLicenseState: response.driversLicenseState
                    };
                }
            });
            return {
                ...state,
                checkCustomerLoading: false,
                checkCustomerResponse: updatedCheckCustomerResponse,
                participantsSpd: updatedParticipantsSpd,
                checkCustomerError: null,
                canDesk: Boolean(canDesk)
            };
        }
        case CHECK_CUSTOMER_SPD_REQUEST: {
            return {
                ...state,
                checkCustomerLoading: true,
                checkCustomerError: null
            };
        }
        case UPDATE_CUSTOMER_SPD: {
            const { customerSpdData: spdData, commonConsumerId } = action.payload;
            const checkCustomerResponse = (state.checkCustomerResponse as Record<string, CustomerSpdResponseItem>)[
                commonConsumerId
            ] || { ...DEFAULT_CHECK_CUSTOMER_SPD_RESPONSE };

            // Convert fields to boolean for checkCustomerResponse to represent updated participant spd state except driversLicenseState
            const checkSpdData = Object.keys(checkCustomerResponse).reduce((acc, key) => {
                const typedKey = key as keyof CustomerSpdResponseItem;

                if (typedKey === 'driversLicenseState') {
                    acc[typedKey] = checkCustomerResponse[typedKey];
                } else {
                    acc[typedKey] =
                        spdData[typedKey] !== undefined && spdData[typedKey] !== null
                            ? spdData[typedKey] !== ''
                            : Boolean(checkCustomerResponse[typedKey]);
                }

                return acc;
            }, {} as CustomerSpdResponseItem);

            return {
                ...state,
                participantsSpd: {
                    ...state.participantsSpd,
                    [commonConsumerId]: spdData
                },
                checkCustomerResponse: {
                    ...state.checkCustomerResponse,
                    [commonConsumerId]: checkSpdData
                }
            };
        }
        case UPDATE_CUSTOMER_SPD_REQUEST: {
            return {
                ...state,
                updateCustomerSpdLoading: true,
                updateCustomerSpdResponse: {},
                updateCustomerSpdError: null
            };
        }
        case UPDATE_CUSTOMER_SPD_SUCCESS: {
            const updatedParticipantsSpd = { ...state.participantsSpd } as { [commonConsumerId: string]: CustomerSpd };
            const updateCustomerSpdPayload = action.meta.customerSpdData;
            const updatedCheckCustomerResponse = { ...state.checkCustomerResponse };
            const updateItems = Array.isArray(action.payload) ? action.payload : [];
            updateItems.forEach((item: { ccid: string }) => {
                // Merging with empty object to ensure empty fields are removed to match with the backend state
                updatedParticipantsSpd[item.ccid] = mergeNonEmptyFields(state.participantsSpd[item.ccid], {});
            });
            updateCustomerSpdPayload.forEach((spdData: CustomerSpd) => {
                if ('driversLicenseState' in spdData) {
                    updatedCheckCustomerResponse[spdData.commonConsumerId] = {
                        ...updatedCheckCustomerResponse[spdData.commonConsumerId],
                        driversLicenseState: spdData.driversLicenseState || undefined
                    };
                }
            });
            return {
                ...state,
                updateCustomerSpdResponse: action.payload,
                updateCustomerSpdLoading: false,
                getCustomerResponse: action.payload ? updatedParticipantsSpd : state.getCustomerResponse,
                participantsSpd: action.payload ? updatedParticipantsSpd : state.participantsSpd,
                checkCustomerResponse: updatedCheckCustomerResponse
            };
        }
        case UPDATE_CUSTOMER_SPD_FAILURE: {
            return {
                ...state,
                updateCustomerSpdError: action.payload,
                updateCustomerSpdLoading: false
            };
        }
        case CUSTOMER_SPD_FAILURE: {
            return {
                ...state,
                getCustomerLoading: false,
                getCustomerError: {
                    ...state.getCustomerError,
                    [action.meta.commonConsumerId]: action.payload
                }
            };
        }
        case CUSTOMER_SPD_SUCCESS: {
            if (action.payload.ok === false) {
                return {
                    ...state,
                    getCustomerLoading: false,
                    getCustomerResponse: {
                        ...state.getCustomerResponse,
                        [action.meta.commonConsumerId]: {}
                    }
                };
            }
            const response = (action.payload as Record<string, CustomerSpd>)[action.meta.commonConsumerId];
            return {
                ...state,
                getCustomerLoading: false,
                getCustomerResponse: {
                    ...state.getCustomerResponse,
                    [action.meta.commonConsumerId]: response
                },
                participantsSpd: {
                    ...state.participantsSpd,
                    [action.meta.commonConsumerId]: mergeNonEmptyFields(
                        response,
                        state.participantsSpd[action.meta.commonConsumerId] || {}
                    )
                },
                getCustomerAPISuccess: {
                    ...state.getCustomerAPISuccess,
                    [action.meta.commonConsumerId]: true
                }
            };
        }
        case CUSTOMER_SPD_REQUEST: {
            const remainingErrors = Object.fromEntries(
                Object.entries(state.getCustomerError).filter(([key]) => key !== action.meta.commonConsumerId)
            );
            return {
                ...state,
                getCustomerLoading: true,
                getCustomerError: remainingErrors
            };
        }
        case SHOW_EDIT_CUSTOMER_SLIDEOUT:
        case HIDE_EDIT_CUSTOMER_SLIDEOUT: {
            return {
                ...state,
                getCustomerError: {},
                checkCustomerError: null
            };
        }
        case CLEAR_CUSTOMER_SPD_ERROR: {
            return {
                ...state,
                getCustomerError: {}
            };
        }
        default: {
            return state;
        }
    }
};
